import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Film, Play, Download, RefreshCw, AlertTriangle,
    CheckCircle2, Clock, FileVideo, Captions, Info,
} from "lucide-react";
import { api, formatApiError, absoluteAssetUrl } from "../lib/api";
import { plainEnglishMessage, isMotionNote, AUTO_FIX_RESOLVABLE_ISSUES, AUTO_FIX_RESOLVABLE_WARNINGS } from "../lib/plainEnglish";
import {
    moduleStatusLabel, moduleStatusPillClass,
    renderStatusLabel, renderStatusPillClass,
} from "../lib/moduleStatus";
import BuildProgressCard from "../components/BuildProgressCard";
import FidelityBanner from "../components/FidelityBanner";
import { TargetLengthControl, TargetVsCurrent } from "../components/TargetLengthControl";
import { useAuth } from "../lib/auth-context";
import { Button } from "../components/ui/button";
import { displayMoney } from "../utils/pricing";
import { AiFixHistory } from "../components/project/AiFixPanel";

// Same-decimals raw-cost helper used by the admin "Show pricing detail"
// verify mode. Mirrors ``Usage.jsx::rawCurrency`` so an auditor can
// cross-check that displayCurrency(raw) == raw × 1.5 to the last cent.
const rawCurrency = (n, digits = 2) => {
    const v = Number.isFinite(n) ? n : 0;
    let d = digits;
    if (v > 0 && Math.abs(v) < 0.01) d = Math.max(d, 7);
    return v.toFixed(d);
};

// Rolled-up cost widget for a single module. Sums provider costs from
// heygen_generations, runway_generations, voice_assets, motion_renders
// (via /modules/{id}/cost-summary) and displays at the 1.5x customer
// price. Backend billing math stays raw. Admins get a "Show pricing
// detail" toggle to reveal raw provider costs beside the charged
// figures — same pattern as the Usage page.
function ModuleCostSummary({ moduleId, isAdmin }) {
    const [summary, setSummary] = useState(null);
    const [showRaw, setShowRaw] = useState(false);
    useEffect(() => {
        let alive = true;
        api.get(`/modules/${moduleId}/cost-summary`)
            .then((r) => { if (alive) setSummary(r.data); })
            .catch(() => {});
        return () => { alive = false; };
    }, [moduleId]);
    if (!summary) return null;
    // THIS build's real cost: only what this render created; reused clips are $0.
    const b = summary.latest_build || {};
    const rows = [
        { key: "trainer", label: "Trainer footage", raw: b.trainer_usd || 0,
          note: b.trainer_clips_new ? `${b.trainer_clips_new} new` : (b.trainer_clips_reused ? `${b.trainer_clips_reused} reused` : "") },
        { key: "cinematic", label: "Cinematic clips", raw: b.cinematic_usd || 0,
          note: b.cinematic_clips_new ? `${b.cinematic_clips_new} new` : "reused" },
        { key: "voice", label: "Voice narration", raw: b.voice_usd || 0, note: b.voice_usd ? "" : "reused" },
    ];
    const rawTotal = b.total_usd || 0;
    const moduleTotal = summary.builds_total_usd || 0;
    return (
        <div
            data-testid="module-cost-summary"
            className="rounded-lg border border-neutral-200 bg-white overflow-hidden"
        >
            <div className="border-b border-neutral-200 px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-neutral-500 flex items-center justify-between">
                <span>Total build cost <span className="normal-case tracking-normal text-[10px] text-neutral-400">(customer price)</span></span>
                {isAdmin && (
                    <button
                        type="button"
                        data-testid="module-cost-toggle-raw"
                        onClick={() => setShowRaw((v) => !v)}
                        className="normal-case tracking-normal text-[10px] font-medium text-neutral-600 hover:text-neutral-900 underline"
                    >
                        {showRaw ? "Hide pricing detail" : "Show pricing detail"}
                    </button>
                )}
            </div>
            <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                {rows.map((r) => (
                    <div key={r.key}>
                        <div className="text-neutral-500">{r.label}</div>
                        <div className="mono text-sm text-neutral-900 mt-0.5" data-testid={`cost-${r.key}`}>
                            ${displayMoney(r.raw)}
                            {r.note && <span className="ml-1 text-[10px] text-neutral-400">{r.note}</span>}
                        </div>
                        {isAdmin && showRaw && (
                            <div
                                className="mt-0.5 text-[10px] text-neutral-500 mono"
                                data-testid={`cost-${r.key}-raw`}
                            >
                                raw ${rawCurrency(r.raw, 4)} × 1.5
                            </div>
                        )}
                    </div>
                ))}
                <div className="col-span-2 md:col-span-4 border-t border-neutral-100 pt-3 flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-widest text-neutral-500">This build</span>
                    <div className="flex flex-col items-end">
                        <span className="mono text-base text-neutral-900" data-testid="cost-total">${displayMoney(rawTotal)}</span>
                        <span className="mt-0.5 text-[10px] text-neutral-500" data-testid="cost-module-total">
                            All {summary.builds?.length || 0} builds: ${displayMoney(moduleTotal)}
                        </span>
                        {isAdmin && showRaw && (
                            <span
                                className="mt-0.5 text-[10px] text-neutral-500 mono"
                                data-testid="cost-total-raw"
                            >
                                raw ${rawCurrency(rawTotal, 4)} × 1.5 = ${displayMoney(rawTotal)}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ---------- List view ----------

export function VideoStudio() {
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/modules-with-renders");
            setModules(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="p-10 text-sm text-neutral-500">Loading…</div>;

    return (
        <div data-testid="video-studio" className="space-y-6">
            <div>
                <div className="text-lg font-medium text-neutral-900">Video Studio</div>
                <div className="mt-1 text-sm text-neutral-500">
                    Turn every approved storyboard into a finished training video with
                    narration and captions.
                </div>
            </div>

            {modules.length === 0 ? (
                <div className="rounded-lg border border-dashed border-neutral-200 bg-white p-12 text-center">
                    <Film size={22} className="mx-auto mb-3 text-neutral-400" />
                    <div className="text-sm font-medium text-neutral-900">No modules yet</div>
                    <p className="mt-1 text-sm text-neutral-500">
                        Create a storyboard from Content → Storyboards, then return here to render it.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="video-studio-grid">
                    {modules.map((m) => (
                        <Link
                            key={m.id}
                            to={`/content/video-studio/${m.id}`}
                            data-testid={`video-studio-card-${m.id}`}
                            className="group rounded-lg border border-neutral-200 bg-white overflow-hidden hover:border-neutral-400 transition-colors"
                        >
                            <div className="relative aspect-video bg-neutral-900 overflow-hidden">
                                {m.thumbnail_signed_url ? (
                                    <img
                                        src={absoluteAssetUrl(m.thumbnail_signed_url)}
                                        alt=""
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <div className="flex h-full w-full items-center justify-center text-neutral-500">
                                        <Film size={28} />
                                    </div>
                                )}
                                <div className="absolute top-2 right-2">
                                    <span
                                        data-testid={`render-status-${m.id}`}
                                        className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-medium ${renderStatusPillClass(m.render_status)}`}
                                    >
                                        {renderStatusLabel(m.render_status)}
                                    </span>
                                </div>
                            </div>
                            <div className="p-4">
                                <div className="text-sm font-medium text-neutral-900 truncate">{m.name}</div>
                                <div className="mt-1 flex items-center gap-3 text-[11px] text-neutral-500">
                                    <span>{m.target_video_minutes || 5} min target</span>
                                    {m.latest_duration_s ? (
                                        <>
                                            <span>·</span>
                                            <span className="mono">{Math.round(m.latest_duration_s)}s rendered</span>
                                        </>
                                    ) : null}
                                    <span>·</span>
                                    <span>{(m.status || "DRAFT").replaceAll("_", " ").toLowerCase()}</span>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}

// ---------- Detail view ----------

export function VideoStudioDetail({ embedded = false, currentDurationS } = {}) {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [fixedKeys, setFixedKeys] = useState({});
    const [typicalNote, setTypicalNote] = useState("");
    useEffect(() => {
        api.get(`/modules/${id}/build-progress`).then((r) => setTypicalNote(r.data?.typical_display || "")).catch(() => {});
    }, [id]);
    const isAdmin = user?.role === "ORG_ADMIN" || user?.role === "LD_ADMIN";
    const [detail, setDetail] = useState(null);
    const [preflight, setPreflight] = useState(null);
    const [renderStatus, setRenderStatus] = useState(null);
    const [history, setHistory] = useState([]);
    const [approval, setApproval] = useState(null);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);
    const [preflightLoading, setPreflightLoading] = useState(false);
    const [autoFixRunning, setAutoFixRunning] = useState(false);
    // Once-per-page-load guard: prevents the silent auto-heal from
    // re-firing on every preflight re-poll. Reset via retryAutoFix().
    const autoFixAttemptedRef = useRef(false);
    // Hard 90s cap on the visible "Auto-fixing…" state. If the backend
    // stalls (rate-limited AI Trainer, slow Cinematic Engine), we drop to the
    // "manual step needed" state instead of spinning forever.
    const autoFixTimeoutRef = useRef(null);
    const [autoFixBlocked, setAutoFixBlocked] = useState(null);

    const loadAll = useCallback(async () => {
        try {
            const [d, rs, hist, appr] = await Promise.all([
                api.get(`/modules/${id}`),
                api.get(`/modules/${id}/render-status`),
                api.get(`/modules/${id}/renders`),
                api.get(`/modules/${id}/approval`).catch(() => ({ data: null })),
            ]);
            setDetail(d.data);
            setRenderStatus(rs.data);
            setHistory(hist.data);
            setApproval(appr.data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [id]);

    const runPreflight = useCallback(async () => {
        setPreflightLoading(true);
        try {
            const { data } = await api.post(`/modules/${id}/render-preflight`);
            setPreflight(data);
            return data;
        } catch (e) {
            toast.error(formatApiError(e));
            return null;
        } finally {
            setPreflightLoading(false);
        }
    }, [id]);

    // Silent proactive auto-fix. FIRE AT MOST ONCE per page load.
    // Backend has a 60s wall-clock cap AND a circuit breaker (2
    // consecutive failures within 5min → refused). Frontend adds:
    //   - once-per-load ref so preflight re-polls never re-trigger.
    //   - 90s visible timeout → drop to "manual step needed" state.
    //   - respect circuit / failed responses; stop spinning immediately.
    const kickBackgroundAutoFix = useCallback(async (pf) => {
        if (!pf) return;
        if (autoFixAttemptedRef.current) return;
        if (autoFixRunning) return;
        const resolvableIssues = AUTO_FIX_RESOLVABLE_ISSUES;
        const resolvableWarnings = AUTO_FIX_RESOLVABLE_WARNINGS;
        const hasIssue = (pf.issues || []).some((it) => resolvableIssues.has(it.kind));
        const hasWarn = (pf.warnings || []).some((w) => resolvableWarnings.has(w.kind));
        if (!hasIssue && !hasWarn) return;
        autoFixAttemptedRef.current = true;
        setAutoFixRunning(true);
        setAutoFixBlocked(null);
        if (autoFixTimeoutRef.current) clearTimeout(autoFixTimeoutRef.current);
        autoFixTimeoutRef.current = setTimeout(() => {
            setAutoFixRunning(false);
            setAutoFixBlocked({
                reason: "timeout",
                message:
                    "We couldn't finish this automatically — a small manual step is needed.",
            });
        }, 90000);
        let response = null;
        try {
            await api.post(`/modules/${id}/fix-visuals`, {}).catch(() => null);
            const { data } = await api.post(`/modules/${id}/auto-fix`);
            response = data;
        } catch {
            // Network / auth error — surface manual state.
            response = { failed: [{ reason: "network" }], circuit: "closed" };
        }
        if (autoFixTimeoutRef.current) {
            clearTimeout(autoFixTimeoutRef.current);
            autoFixTimeoutRef.current = null;
        }
        const circuitOpen = response?.circuit === "open";
        const failedList = response?.failed || [];
        const anyFailed = failedList.length > 0;
        if (circuitOpen || anyFailed) {
            const firstFail = failedList[0] || {};
            setAutoFixBlocked({
                reason: circuitOpen ? "circuit_open" : (firstFail.reason || "failed"),
                retry_after: firstFail.retry_after || null,
                message:
                    firstFail.message ||
                    "We couldn't finish this automatically — a small manual step is needed.",
            });
        }
        setAutoFixRunning(false);
        try { await runPreflight(); } catch { /* keep going */ }
    }, [id, autoFixRunning, runPreflight]);

    const retryAutoFix = useCallback(async () => {
        autoFixAttemptedRef.current = false;
        setAutoFixBlocked(null);
        const pf = await runPreflight();
        await kickBackgroundAutoFix(pf);
    }, [runPreflight, kickBackgroundAutoFix]);

    // Cleanup on unmount so a stale timeout can't flip UI on a
    // different module.
    useEffect(() => () => {
        if (autoFixTimeoutRef.current) clearTimeout(autoFixTimeoutRef.current);
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);
    // Page load is read-only: auto-fix only via the explicit retry button.
    useEffect(() => { runPreflight(); }, [runPreflight]);

    // Banners only for a genuinely active job: never once terminal, and never a
    // job older than the module's latest READY render (phantom "resolving visual").
    const TERMINAL_JOB = ["COMPLETED", "FAILED", "CANCELLED"];
    const rawJob = renderStatus?.current_job;
    const latestReadyAt = renderStatus?.render_status === "READY" ? (renderStatus?.latest_render?.created_at || "") : "";
    // Any non-terminal job blocks new renders → it always gets a Cancel button.
    const activeJob = rawJob && !TERMINAL_JOB.includes(rawJob.status) ? rawJob : null;
    const currentJob = activeJob && !(latestReadyAt && (activeJob.created_at || "") <= latestReadyAt) ? activeJob : null;
    const currentJobId = currentJob?.id;

    // Poll active render job
    useEffect(() => {
        if (!currentJobId) return;
        const t = setInterval(async () => {
            try {
                const { data } = await api.get(`/render-jobs/${currentJobId}`);
                setRenderStatus((prev) => ({
                    ...(prev || {}),
                    current_job:
                        TERMINAL_JOB.includes(data.status) ? null : data,
                }));
                if (TERMINAL_JOB.includes(data.status)) {
                    toast[data.status === "COMPLETED" ? "success" : "error"](
                        data.status === "COMPLETED"
                            ? "Render completed"
                            : `Render ${data.status.toLowerCase()}: ${data.stage_note || ""}`,
                    );
                    await loadAll();
                    setRunning(false);
                }
            } catch { /* keep polling */ }
        }, 2500);
        return () => clearInterval(t);
    }, [currentJobId, loadAll]);

    // Hoist auth + STORYBOARD_APPROVED so `startRender` closure sees it.
    // Original site of these declarations moved to right after we set
    // `mrs`; see below.
    const startRender = async () => {
        const status = detail?.module?.status;
        const approved = status === "APPROVED" || status === "PUBLISHED";
        if (!approved) {
            toast.error("Review and approve the storyboard first.");
            return;
        }
        if (!preflight?.ok) {
            toast.error("A few scenes still need finishing — give it a moment.");
            return;
        }
        setRunning(true);
        try {
            await api.post(`/modules/${id}/render`, { trigger: "user_click" });
            toast.success("Render queued");
            await loadAll();
        } catch (e) {
            const d = e?.response?.data?.detail;
            if (typeof d === "object" && d?.issues) {
                toast.error((d.issues[0]?.message) || "Video health check found an issue.");
            } else if (typeof d === "object" && d?.already_running) {
                toast.error("A render is already in progress.");
            } else {
                toast.error(formatApiError(e));
            }
            setRunning(false);
        }
    };

    const cancelRender = async () => {
        const jid = activeJob?.id;
        if (!jid) return;
        try {
            await api.post(`/render-jobs/${jid}/cancel`);
            toast.success("Cancelled");
            await loadAll();
            setRunning(false);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const doApprovalSubmit = async () => {
        try { await api.post(`/modules/${id}/approval`, { note: "Ready for review" }); toast.success("Submitted for approval"); await loadAll(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const doApprove = async () => {
        try { await api.post(`/approval-requests/${approval?.current_request?.id}/approve`); toast.success("Approved"); await loadAll(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const doRequestChanges = async () => {
        const note = window.prompt("What needs to change?");
        if (!note) return;
        try { await api.post(`/approval-requests/${approval?.current_request?.id}/request-changes`, { note }); toast.success("Changes requested"); await loadAll(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const doPublish = async () => {
        try { await api.post(`/modules/${id}/publish`); toast.success("Published"); await loadAll(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const doWithdraw = async () => {
        try { await api.post(`/approval-requests/${approval?.current_request?.id}/withdraw`); toast.success("Withdrawn"); await loadAll(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const saveTarget = async (v) => {
        try {
            await api.patch(`/modules/${id}`, { target_video_minutes: v });
            const { data } = await api.get(`/modules/${id}`);
            setDetail(data);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (loading || !detail) return <div className="p-10 text-sm text-neutral-500">Loading…</div>;

    const m = detail.module;
    const latest = renderStatus?.latest_render;
    const mrs = m.render_status || "NEVER_RENDERED";
    // Gate: video generation is only allowed after the storyboard has
    // been approved. Pre-approval states (STORYBOARD_READY,
    // AWAITING_APPROVAL, CHANGES_REQUESTED, DRAFT, STRUCTURING) show
    // a "Review and approve storyboard" CTA instead of the render
    // button.
    // Real storyboard state: an approved/locked current version counts even while the
    // module itself is building / failed / in review.
    const cv = detail?.current_version || {};
    const STORYBOARD_APPROVED = m.status === "APPROVED" || m.status === "PUBLISHED"
        || ["APPROVED", "LOCKED", "PUBLISHED"].includes(cv.status) || !!cv.approved_at || !!cv.locked_at;
    const STORYBOARD_PRE_APPROVAL = ["DRAFT", "STRUCTURING", "STORYBOARD_READY",
        "AWAITING_APPROVAL", "CHANGES_REQUESTED"].includes(m.status);
    // Scene counter — reads live from ``current_version.scenes`` so the
    // widget matches the storyboard-editor's truth. When the module is
    // pre-approval we surface "approved-scenes / total"; once APPROVED
    // we switch to preflight's "scenes-ready / total" (which counts
    // assets, not approvals).
    const versionScenes = detail?.current_version?.scenes || [];
    const approvedScenes = versionScenes.filter(
        (s) => (s?.approval_state || "").toUpperCase() === "APPROVED").length;
    const scenesTotalDisplay = versionScenes.length || (preflight?.scenes_total || 0);
    const scenesReadyDisplay = STORYBOARD_APPROVED
        ? (preflight?.scenes_ready || 0)
        : approvedScenes;
    // Stale-render detection: a render whose ``version_id`` doesn't
    // match the module's current storyboard version is from a previous
    // (pre-edit) storyboard and must NOT be surfaced as playable. This
    // covers the legacy case where a render was made before the
    // storyboard hard-gate landed. We fold the check into
    // STORYBOARD_APPROVED gating so the preview only renders when the
    // module is approved AND the render matches the current version.
    const currentVersionId = detail.current_version?.id || m.current_version_id;
    const renderVersionMatches = !latest || !currentVersionId ||
        (latest.version_id === currentVersionId
         || latest.module_version_id === currentVersionId
         || latest.storyboard_version_id === currentVersionId);
    const RENDER_IS_STALE = !!latest && !renderVersionMatches;
    // When the module already has a valid current READY render that
    // matches the current storyboard version, the finished-video block
    // below is the source of truth. We MUST suppress the progress card,
    // the storyboard-approval warning, and any "Generating" copy so the
    // user isn't shown a completed video alongside stale "still cooking"
    // UX.
    const RENDER_READY_CURRENT =
        mrs === "READY" && !!latest && !RENDER_IS_STALE && STORYBOARD_APPROVED;

    return (
        <div data-testid="video-studio-detail" className="space-y-4">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                    {!embedded && <Button variant="ghost" size="sm" onClick={() => nav("/videos")}
                        className="-ml-2 mt-1 text-neutral-600 hover:text-neutral-900"
                        data-testid="back-to-video-studio">
                        <ArrowLeft size={14} className="mr-1" /> Videos
                    </Button>}
                    <div>
                        <div className="text-lg font-medium text-neutral-900 leading-tight">{m.name}</div>
                        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-neutral-500">
                            <span>{detail.document_title || "—"}</span>
                            {detail.current_version && (
                                <>
                                    <span>·</span>
                                    <span>v{detail.current_version.version_number}</span>
                                </>
                            )}
                            <span
                                data-testid="module-render-status"
                                className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-medium ${renderStatusPillClass(mrs)}`}
                            >
                                {renderStatusLabel(mrs)}
                            </span>
                        </div>
                        {mrs === "FAILED" && !activeJob && renderStatus?.last_failure && (
                            <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-800 max-w-[720px]"
                                data-testid="render-failure-reason">
                                <span className="font-medium">Build failed:</span>{" "}
                                {renderStatus.last_failure.failure_reason}
                                {(renderStatus.last_failure.failure_stage || renderStatus.last_failure.failure_scene) && (
                                    <span className="text-red-600">
                                        {" "}· stage {renderStatus.last_failure.failure_stage || "—"}
                                        {renderStatus.last_failure.failure_scene ? ` · scene ${renderStatus.last_failure.failure_scene}` : ""}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className={`flex items-center gap-2 ${embedded ? "hidden" : ""}`}>
                    <Button size="sm" variant="outline" onClick={retryAutoFix}
                        data-testid="studio-auto-fix-btn">
                        <RefreshCw size={13} className="mr-1.5" /> Fix problems
                    </Button>
                    <Button size="sm" variant="outline" data-testid="studio-generate-voices-btn"
                        onClick={async () => {
                            try {
                                await api.post(`/modules/${id}/generate-all-voices`, { confirm: true });
                                toast.success("Generating all voices");
                            } catch (e) { toast.error(formatApiError(e)); }
                        }}>
                        Generate all voices
                    </Button>
                    {STORYBOARD_APPROVED && (
                        <Button size="sm" variant="outline" onClick={runPreflight} disabled={preflightLoading}
                            data-testid="rerun-preflight-btn">
                            <RefreshCw size={12} className={`mr-1.5 ${preflightLoading ? "animate-spin" : ""}`} />
                            Re-check
                        </Button>
                    )}
                    {activeJob ? (
                        <Button size="sm" variant="outline" onClick={cancelRender} data-testid="cancel-render-btn">
                            Cancel render
                        </Button>
                    ) : STORYBOARD_PRE_APPROVAL ? (
                        <Button
                            size="sm"
                            onClick={() => nav(`/content/storyboards/${id}`)}
                            data-testid="review-storyboard-btn"
                            className="bg-amber-500 text-white hover:bg-amber-600"
                        >
                            Review &amp; approve storyboard
                        </Button>
                    ) : STORYBOARD_APPROVED ? (
                        <Button
                            size="sm"
                            onClick={startRender}
                            disabled={!preflight?.ok || running}
                            data-testid="start-render-btn"
                            className="bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40"
                        >
                            <Play size={12} className="mr-1.5" />
                            {latest ? "Render again" : "Render module"}
                        </Button>
                    ) : null}
                </div>
            </div>

            {/* Fidelity contract banner (Phase 5.0) — yellow for image
                fallback, red for SCRIPT_DRIFT, green pill for clean. */}
            <div className="rounded-lg border border-neutral-200 bg-white p-4 flex flex-wrap items-center gap-4" data-testid="video-studio-length">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Video length</div>
                <div className="flex-1 min-w-[260px] max-w-[460px]">
                    <TargetLengthControl
                        value={detail.target_video_minutes ?? m.target_video_minutes}
                        onChange={saveTarget}
                        testIdPrefix="studio-target-length"
                    />
                </div>
                <TargetVsCurrent targetMinutes={detail.target_video_minutes ?? m.target_video_minutes}
                    estimatedSeconds={detail.estimated_duration_s} testId="studio-target-vs-current" />
            </div>

            <FidelityBanner moduleId={id} canRetry={isAdmin} currentDurationS={currentDurationS} />

            <div className="grid grid-cols-12 gap-4">
                {/* Left — main content */}
                <div className="col-span-8 space-y-4">
                    {/* Build progress card — mounted while the module is
                        still cooking. Hidden once a current READY render
                        exists so the finished video is the sole source
                        of truth (no more "still generating" copy on a
                        finished module). */}
                    {!RENDER_READY_CURRENT && (
                    <BuildProgressCard
                        moduleId={id}
                        isAdmin={isAdmin}
                        renderGated={!STORYBOARD_APPROVED}
                        onReady={(_p, clicked) => {
                            // Never auto-trigger render before approval —
                            // that was the flow that stalled at
                            // "queuing up scenes and generating a full video"
                            // because auto_render fires only on APPROVED
                            // now. Users must approve the storyboard first.
                            if (clicked && preflight?.ok && STORYBOARD_APPROVED) {
                                startRender();
                            }
                        }}
                    />
                    )}
                    {/* 1A.10 approval strip */}
                    {approval && (
                        <div data-testid="approval-strip" className="rounded-lg border border-neutral-200 bg-white p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-[11px] font-semibold uppercase tracking-widest text-neutral-500">Approval workflow</div>
                                    <div className="mt-1 text-sm font-medium text-neutral-900">
                                        Status: <span
                                            className={`ml-1 inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium ${moduleStatusPillClass(approval.status)}`}
                                            data-testid="module-status"
                                            title={approval.status}
                                        >
                                            {moduleStatusLabel(approval.status)}
                                        </span>
                                    </div>
                                    <div className="mt-1 text-[11px] text-neutral-500">
                                        {approval.submitted_at && <>Submitted {new Date(approval.submitted_at).toLocaleString()} · </>}
                                        {approval.reviewed_at && <>Reviewed {new Date(approval.reviewed_at).toLocaleString()} · </>}
                                        {approval.published_at && <>Published {new Date(approval.published_at).toLocaleString()}</>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {approval.can_submit && <Button size="sm" onClick={doApprovalSubmit} data-testid="approval-submit-btn" className="bg-neutral-900 text-white hover:bg-neutral-800">Submit for approval</Button>}
                                    {approval.can_withdraw && <Button size="sm" variant="outline" onClick={doWithdraw} data-testid="approval-withdraw-btn">Withdraw</Button>}
                                    {approval.can_approve && <Button size="sm" onClick={doApprove} data-testid="approval-approve-btn" className="bg-emerald-600 text-white hover:bg-emerald-700">Approve</Button>}
                                    {approval.can_request_changes && <Button size="sm" variant="outline" onClick={doRequestChanges} data-testid="approval-request-changes-btn">Request changes</Button>}
                                    {approval.can_publish && <Button size="sm" onClick={doPublish} data-testid="approval-publish-btn" className="bg-neutral-900 text-white hover:bg-neutral-800">Publish</Button>}
                                </div>
                            </div>
                            {approval.blocking_reasons?.length > 0 && (
                                <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                                    <div className="font-medium">Almost there — one small step:</div>
                                    <ul className="list-disc pl-4 mt-1 space-y-0.5">
                                        {approval.blocking_reasons.map((r, i) => <li key={i}>{r}</li>)}
                                    </ul>
                                </div>
                            )}
                            {approval.current_request?.decision_note && (
                                <div className="mt-3 rounded-md bg-neutral-50 p-2 text-xs text-neutral-700">
                                    Reviewer note: {approval.current_request.decision_note}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Active job banner */}
                    {currentJob && (
                        <div data-testid="render-progress" className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex items-center gap-3 text-sm text-blue-900">
                            <RefreshCw size={14} className="animate-spin shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="font-medium">Rendering module…</div>
                                <div className="mono text-xs text-blue-700 truncate">
                                    stage: {currentJob.progress_stage_string || currentJob.stage_note || currentJob.status}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Latest render — hidden pre-approval OR when the
                        render's storyboard version doesn't match the
                        current storyboard (stale legacy render). */}
                    {STORYBOARD_APPROVED && !RENDER_IS_STALE && (
                    <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
                        <div className="border-b border-neutral-200 px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-neutral-500">
                            Current render
                        </div>
                        {latest ? (
                            <div className="p-4 space-y-3" data-testid="current-render">
                                <div className="aspect-video rounded-lg overflow-hidden bg-neutral-900">
                                    <video
                                        src={`${absoluteAssetUrl(latest.signed_url)}${latest.signed_url?.includes('?') ? '&' : '?'}v=${latest.id || latest.created_at || ''}`}
                                        controls
                                        preload="metadata"
                                        className="w-full h-full"
                                        data-testid="current-render-video"
                                        key={latest.id || latest.created_at}
                                    >
                                        {latest.captions_signed_url && (
                                            <track
                                                default
                                                kind="captions"
                                                srcLang="en"
                                                src={absoluteAssetUrl(latest.captions_signed_url)}
                                            />
                                        )}
                                    </video>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-neutral-500">
                                    <span className="mono">{Math.round(latest.duration_s)}s</span>
                                    <span>·</span>
                                    <span className="mono">{latest.resolution}</span>
                                    <span>·</span>
                                    <span className="mono">{(latest.file_size_bytes / 1024 / 1024).toFixed(1)} MB</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <a
                                        href={absoluteAssetUrl(latest.signed_url)}
                                        download
                                        data-testid="download-mp4-btn"
                                        className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-xs text-neutral-700 hover:bg-neutral-50"
                                    >
                                        <FileVideo size={12} /> Download mp4
                                    </a>
                                    {latest.captions_signed_url && (
                                        <a
                                            href={absoluteAssetUrl(latest.captions_signed_url)}
                                            download
                                            data-testid="download-vtt-btn"
                                            className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-xs text-neutral-700 hover:bg-neutral-50"
                                        >
                                            <Captions size={12} /> Download captions
                                        </a>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="p-10 text-center text-sm text-neutral-500" data-testid="no-render-yet">
                                <FileVideo size={22} className="mx-auto mb-2 text-neutral-400" />
                                No video built yet. Approve your scenes and start a build.
                            </div>
                        )}
                    </div>
                    )}

                    {/* Pre-approval block: replace preview with review CTA */}
                    {(!STORYBOARD_APPROVED || RENDER_IS_STALE) && (
                        <div
                            data-testid="video-studio-approval-required"
                            className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900 flex flex-col items-start gap-3"
                        >
                            <div className="font-semibold text-base">
                                {RENDER_IS_STALE && STORYBOARD_APPROVED
                                    ? "Outdated render"
                                    : "Storyboard approval required"}
                            </div>
                            <div>
                                {RENDER_IS_STALE && STORYBOARD_APPROVED
                                    ? "This module has an outdated render from an earlier storyboard version. Build a fresh video to see the current storyboard."
                                    : "Every render preview and video-build action is locked until the current storyboard is approved. Head over to the storyboard editor to review each scene."}
                            </div>
                            <Button
                                size="sm"
                                onClick={() => nav(`/content/storyboards/${id}`)}
                                data-testid="video-studio-review-cta"
                                className="bg-amber-500 text-white hover:bg-amber-600"
                            >
                                Review &amp; approve storyboard
                            </Button>
                        </div>
                    )}

                    {/* Total build cost — 1.5x display of provider raw costs */}
                    {STORYBOARD_APPROVED && (
                        <ModuleCostSummary moduleId={id} isAdmin={isAdmin} />
                    )}

                    {/* History */}
                    {STORYBOARD_APPROVED && history.length > 0 && (
                        <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden" data-testid="render-history">
                            <div className="border-b border-neutral-200 px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-neutral-500">
                                Video build history · {history.length}
                            </div>
                            <div className="divide-y divide-neutral-100">
                                {history.map((r) => (
                                    <div
                                        key={r.id}
                                        data-testid={`history-row-${r.id}`}
                                        className="flex items-center gap-3 px-4 py-3"
                                    >
                                        <div className="w-24 aspect-video rounded overflow-hidden bg-neutral-900 shrink-0">
                                            {r.thumbnail_signed_url ? (
                                                <img
                                                    src={absoluteAssetUrl(r.thumbnail_signed_url)}
                                                    alt=""
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-neutral-500">
                                                    <Film size={14} />
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 text-sm text-neutral-800">
                                                <span className="mono text-xs">{new Date(r.created_at).toLocaleString()}</span>
                                                {r.is_current && (
                                                    <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-emerald-50 text-emerald-700">
                                                        current
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-0.5 text-xs text-neutral-500 mono">
                                                {Math.round(r.duration_s)}s · {r.resolution} ·
                                                {" "}{(r.file_size_bytes / 1024 / 1024).toFixed(1)} MB
                                            </div>
                                        </div>
                                        <a
                                            href={absoluteAssetUrl(r.signed_url)}
                                            download
                                            className="inline-flex items-center gap-1 rounded-md border border-neutral-200 px-2.5 py-1 text-xs text-neutral-700 hover:bg-neutral-50"
                                            data-testid={`download-history-${r.id}`}
                                        >
                                            <Download size={11} /> mp4
                                        </a>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <AiFixHistory moduleId={id} />
                </div>

                {/* Right — preflight panel */}
                <div className="col-span-4">
                    <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden sticky top-4">
                        <div className="border-b border-neutral-200 px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-neutral-500 flex items-center justify-between">
                            <span>Video health check</span>
                            {preflight && (
                                <span
                                    data-testid="preflight-verdict"
                                    className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-medium ${
                                        preflight.ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"
                                    }`}
                                >
                                    {preflight.ok ? <CheckCircle2 size={10} /> : <AlertTriangle size={10} />}
                                    {preflight.ok ? "All good" : "Needs a moment"}
                                    {` · ${preflight.scenes_ready} of ${scenesTotalDisplay} storyboard scenes ready`}
                                    {preflight.image_coverage && (
                                        <span data-testid="preflight-image-coverage">
                                            {` · SOP images ${Math.round(preflight.image_coverage.pct)}% (${preflight.image_coverage.used}/${preflight.image_coverage.total})`}
                                        </span>
                                    )}
                                </span>
                            )}
                        </div>
                        <div className="p-4 text-sm space-y-3">
                            {preflightLoading || !preflight ? (
                                <div className="text-neutral-500 text-xs">Checking your video…</div>
                            ) : (
                                <>
                                    <div className="flex items-center justify-between text-xs text-neutral-600">
                                        <span>Status</span>
                                        <span
                                            className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-medium ${moduleStatusPillClass(preflight.module_status)}`}
                                            title={preflight.module_status || "—"}
                                            data-testid="preflight-module-status"
                                        >
                                            {moduleStatusLabel(preflight.module_status)}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs text-neutral-600">
                                        <span>Scenes ready</span>
                                        <span className="mono">
                                            {preflight.scenes_ready} / {scenesTotalDisplay}
                                        </span>
                                    </div>

                                    {!embedded && preflight.issues?.length > 0 && (
                                        <div data-testid="preflight-issues" className="space-y-2">
                                            <div className="text-[10px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-2">
                                                <span>{autoFixRunning ? "Fixing problems…" : (renderStatus?.current_job ? "Building your video…" : `Needs attention · ${preflight.issues.length}`)}</span>
                                                {autoFixRunning && (
                                                    <span
                                                        data-testid="auto-fix-progress"
                                                        className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[9px] font-medium text-emerald-700 normal-case tracking-normal"
                                                    >
                                                        <RefreshCw size={9} className="animate-spin" />
                                                        Working on it…
                                                    </span>
                                                )}
                                            </div>
                                            {autoFixBlocked && !autoFixRunning && latest && (
                                                <div
                                                    data-testid="auto-fix-manual-state"
                                                    className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 flex items-start gap-2"
                                                >
                                                    <Info size={12} className="mt-0.5 shrink-0" />
                                                    <div className="flex-1">
                                                        <div className="font-medium">{autoFixBlocked.message}</div>
                                                        {autoFixBlocked.retry_after ? (
                                                            <div className="text-[10px] text-amber-800 mt-0.5">
                                                                We'll try again in {autoFixBlocked.retry_after}s.
                                                            </div>
                                                        ) : null}
                                                        <button
                                                            data-testid="auto-fix-retry-btn"
                                                            type="button"
                                                            onClick={retryAutoFix}
                                                            className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-white px-2 py-1 text-[10px] font-medium text-amber-900 ring-1 ring-inset ring-amber-300 hover:bg-amber-100"
                                                        >
                                                            <RefreshCw size={10} /> Try again
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            {latest && preflight.issues.map((it, i) => (
                                                <div
                                                    key={i}
                                                    data-testid={`preflight-issue-${i}`}
                                                    className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-900 flex items-start gap-2"
                                                >
                                                    <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                                                    <div className="flex-1">{plainEnglishMessage(it)}</div>
                                                    {fixedKeys[`i-${it.scene_id || i}`] ? (
                                                        <span data-testid={`preflight-issue-fixed-${i}`} className="shrink-0 rounded bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800">Fixed</span>
                                                    ) : (
                                                    <button
                                                        type="button"
                                                        data-testid={`preflight-issue-fix-${i}`}
                                                        className="shrink-0 rounded border border-red-300 px-2 py-0.5 text-[11px] hover:bg-red-100"
                                                        onClick={async () => {
                                                            try {
                                                                const { data } = await api.post(`/modules/${id}/fix-visuals`,
                                                                    it.scene_id ? { scene_ids: [it.scene_id] } : {});
                                                                if (!it.scene_id) await api.post(`/modules/${id}/auto-fix`).catch(() => null);
                                                                toast.success(data?.actions?.[0]?.action || "Fixed — it will be used on the next render");
                                                                setFixedKeys((f) => ({ ...f, [`i-${it.scene_id || i}`]: true }));
                                                                runPreflight();
                                                            } catch (e) { toast.error(formatApiError(e)); }
                                                        }}
                                                    >
                                                        {it.scene_id ? "Regenerate visual" : "Fix"}
                                                    </button>
                                                    )}
                                                </div>
                                            ))}
                                            {!latest && renderStatus?.current_job && (
                                                <div
                                                    data-testid="first-time-progress-msg"
                                                    className="rounded-md border border-blue-200 bg-blue-50 p-2 text-xs text-blue-900 flex items-start gap-2"
                                                >
                                                    <RefreshCw size={12} className="mt-0.5 shrink-0 animate-spin" />
                                                    <div>
                                                        <div className="font-medium">Filming your trainer and adding scenes…</div>
                                                        <div className="mt-0.5 text-[11px] text-blue-800">
                                                            First builds typically take 8–12 minutes. We'll keep working in the background — you can safely leave this page.
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {!embedded && user?.role === "ORG_ADMIN" && (
                                        <button
                                            type="button"
                                            data-testid="reset-render-state-btn"
                                            className="rounded border border-slate-300 px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-100"
                                            onClick={async () => {
                                                if (!window.confirm("Cancel any running build and unstick this video?")) return;
                                                try {
                                                    const { data } = await api.post(`/modules/${id}/reset-render-state`);
                                                    toast.success(`Video unstuck (${data.jobs_failed} build(s) cleared)`);
                                                    window.location.reload();
                                                } catch (e) { toast.error(formatApiError(e)); }
                                            }}
                                        >
                                            Unstick video
                                        </button>
                                    )}

                                    {!embedded && preflight.warnings?.filter((w) => !isMotionNote(w)).length > 0 && (
                                        <div data-testid="preflight-warnings" className="space-y-2">
                                            <div className="text-[10px] uppercase tracking-widest text-amber-800 font-semibold">
                                                Warnings · {preflight.warnings.filter((w) => !isMotionNote(w)).length}
                                            </div>
                                            {preflight.warnings.filter((w) => !isMotionNote(w)).map((w, i) => (
                                                <div
                                                    key={i}
                                                    data-testid={`preflight-warning-${i}`}
                                                    className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900 flex items-start gap-2"
                                                >
                                                    <Info size={12} className="mt-0.5 shrink-0" />
                                                    <div className="flex-1">{plainEnglishMessage(w)}</div>
                                                    {fixedKeys[`w-${w.scene_id}`] && (
                                                        <span data-testid={`preflight-warning-fixed-${i}`} className="shrink-0 rounded bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800">Fixed</span>
                                                    )}
                                                    {!fixedKeys[`w-${w.scene_id}`] && w.scene_id && w.kind === "scene_image_missing_from_render" && (
                                                        <button
                                                            type="button"
                                                            data-testid={`preflight-warning-fix-${i}`}
                                                            className="shrink-0 rounded border border-amber-300 px-2 py-0.5 text-[11px] hover:bg-amber-100"
                                                            onClick={async () => {
                                                                try {
                                                                    await api.post(`/modules/${id}/fix-visuals`, { scene_ids: [w.scene_id] });
                                                                    toast.success("Visual fixed — it will be used on the next render");
                                                                    setFixedKeys((f) => ({ ...f, [`w-${w.scene_id}`]: true }));
                                                                    runPreflight();
                                                                } catch (e) { toast.error(formatApiError(e)); }
                                                            }}
                                                        >
                                                            Regenerate visual
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {!embedded && preflight.warnings?.filter(isMotionNote).map((w, i) => (
                                        <div key={`note-${i}`} data-testid={`preflight-note-${i}`} className="flex items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-2 text-xs text-neutral-600">
                                            <Info size={12} className="shrink-0 text-neutral-400" />
                                            {w.scene_number ? `Scene ${w.scene_number} · motion graphic` : plainEnglishMessage(w)}
                                        </div>
                                    ))}

                                    {preflight.ok && preflight.issues.length === 0 && preflight.warnings.filter((w) => !isMotionNote(w)).length === 0 && (
                                        <div data-testid="preflight-clear" className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900 flex items-start gap-2">
                                            <CheckCircle2 size={12} className="mt-0.5 shrink-0" />
                                            <span>No blockers — ready to render.</span>
                                        </div>
                                    )}
                                    {preflight.ok && preflight.issues.length === 0 && autoFixRunning && (
                                        <div
                                            data-testid="auto-fix-progress-empty"
                                            className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-[10px] font-medium text-emerald-700"
                                        >
                                            <RefreshCw size={10} className="animate-spin" />
                                            Auto-fixing…
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="border-t border-neutral-200 px-4 py-3 text-[11px] text-neutral-500 flex items-center gap-2">
                            <Clock size={11} />
                            <span data-testid="render-typical-time">Renders run one at a time. {typicalNote || "Timing appears after the first finished build."}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VideoStudio;
